import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import { AlertTriangle } from 'lucide-react'

// ANU-91 — BLIND MAN overlay.
// SAFETY-CRITICAL. Full-screen, fixed, z-index 9999, NO dismiss path.
// Subscribes to events.is_paused for an event the viewer is in. Renders
// over every other surface (PlayerView, CommandCenter, RefereeView) so
// nobody can miss a real-world ceasefire call.
//
// When the GM resumes (events.is_paused -> FALSE), the overlay unmounts
// with a short fade. The display itself does NOT extend any expires_at
// timers DB-side — that's explicitly out of scope per the ticket. The
// only client-side accommodation is CountdownTimer's isPaused prop,
// which freezes the displayed value without touching the deadline.
export default function BlindManOverlay({ eventId }) {
  const [isPaused, setIsPaused] = useState(false)
  const [reason, setReason] = useState(null)
  const [calledByCallsign, setCalledByCallsign] = useState(null)
  const [fading, setFading] = useState(false)

  useEffect(() => {
    if (!eventId) return
    let cancelled = false

    const load = async () => {
      const { data } = await supabase
        .from('events')
        .select('is_paused, blind_man_reason, blind_man_called_by')
        .eq('id', eventId)
        .limit(1)
      if (cancelled) return
      const ev = data?.[0]
      if (!ev) return
      setIsPaused(!!ev.is_paused)
      setReason(ev.blind_man_reason || null)
      if (ev.blind_man_called_by) {
        const { data: udata } = await supabase
          .from('users')
          .select('callsign, display_name')
          .eq('id', ev.blind_man_called_by)
          .limit(1)
        if (!cancelled) {
          const u = udata?.[0]
          setCalledByCallsign(u?.callsign || u?.display_name || null)
        }
      } else {
        if (!cancelled) setCalledByCallsign(null)
      }
    }
    load()

    const channel = supabase
      .channel(`blind-man-${eventId}`)
      .on('postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'events', filter: `id=eq.${eventId}` },
        (payload) => {
          const next = payload.new
          if (next?.is_paused === false && isPaused) {
            // Fade-out shim so the screen doesn't flash to normal play
            // the same tick the ceasefire ends. 250ms is the standard
            // "yes that happened" perceptual delay.
            setFading(true)
            setTimeout(() => {
              setIsPaused(false)
              setReason(null)
              setCalledByCallsign(null)
              setFading(false)
            }, 250)
          } else {
            setIsPaused(!!next?.is_paused)
            setReason(next?.blind_man_reason || null)
            if (next?.blind_man_called_by) {
              supabase.from('users').select('callsign, display_name').eq('id', next.blind_man_called_by).limit(1)
                .then(({ data }) => {
                  if (cancelled) return
                  const u = data?.[0]
                  setCalledByCallsign(u?.callsign || u?.display_name || null)
                })
            }
          }
        })
      .subscribe()

    return () => {
      cancelled = true
      try { supabase.removeChannel(channel) } catch { /* ignore */ }
    }
  // isPaused intentionally omitted from deps — we only re-subscribe on eventId.
  // The handler reads the latest state via setIsPaused() callbacks.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId])

  if (!isPaused && !fading) return null

  return (
    <div
      role="alert"
      aria-live="assertive"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: '#000',
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        opacity: fading ? 0 : 1,
        transition: 'opacity 0.25s ease-out',
        // Pulsing red border, per spec — eye-grabbing without flashing
        // (no strobe), and the inset gives it a "warning frame" feel.
        boxShadow: 'inset 0 0 0 4px #cc0000',
        animation: 'blind-man-pulse 1.4s ease-in-out infinite',
      }}
    >
      <style>{`
        @keyframes blind-man-pulse {
          0%, 100% { box-shadow: inset 0 0 0 4px #cc0000; }
          50%      { box-shadow: inset 0 0 0 4px #ff4040, inset 0 0 80px rgba(255,42,42,.35); }
        }
      `}</style>

      <AlertTriangle size={56} style={{ color: '#ff4040', marginBottom: 20 }} />

      <h1 style={{
        fontFamily: "'Rajdhani', 'Saira Condensed', sans-serif",
        fontWeight: 900,
        color: '#ff4040',
        fontSize: 'min(18vw, 120px)',
        letterSpacing: '0.12em',
        textAlign: 'center',
        margin: 0,
        lineHeight: 1,
        textShadow: '0 0 24px rgba(255,42,42,.6)',
      }}>
        BLIND MAN
      </h1>

      <p style={{
        fontFamily: "'Rajdhani', sans-serif",
        fontWeight: 700,
        color: '#fff',
        fontSize: 'min(5vw, 24px)',
        letterSpacing: '0.18em',
        textTransform: 'uppercase',
        marginTop: 18,
        textAlign: 'center',
      }}>
        CEASEFIRE — STOP ALL PLAY
      </p>

      {reason && (
        <p style={{
          fontFamily: "'JetBrains Mono', monospace",
          color: '#ffd0d0',
          fontSize: 14,
          marginTop: 28,
          maxWidth: 520,
          textAlign: 'center',
          padding: '12px 16px',
          background: 'rgba(255,42,42,.1)',
          border: '1px solid rgba(255,42,42,.35)',
        }}>
          {reason}
        </p>
      )}

      <p style={{
        fontFamily: "'JetBrains Mono', monospace",
        color: '#888',
        fontSize: 12,
        letterSpacing: '0.1em',
        marginTop: 32,
        textTransform: 'uppercase',
        textAlign: 'center',
      }}>
        {calledByCallsign ? `Called by ${calledByCallsign}` : 'Safety hold in effect'} · Wait for GM to lift
      </p>
    </div>
  )
}
